import { useEffect, useMemo, useState } from 'react';

export interface Holiday {
  date: Date;
  name: string;
}

// Cache for the Holidays class to avoid re-importing
let HolidaysClass:
  | typeof import('date-holidays').default
  | null = null;

async function getHolidaysClass(): Promise<
  typeof import('date-holidays').default | null
> {
  if (HolidaysClass) return HolidaysClass;
  try {
    const mod = await import('date-holidays');
    HolidaysClass =
      mod.default ||
      (mod as unknown as typeof import('date-holidays').default);
    return HolidaysClass;
  } catch (err) {
    console.error(
      '[Holidays] Failed to load date-holidays library:',
      err
    );
    return null;
  }
}

/**
 * Get holidays for a given month and region.
 * Uses date-holidays library to compute holidays client-side.
 *
 * @param year - Full year (e.g., 2026)
 * @param month - 0-indexed month (0-11)
 * @param region - ISO country code (e.g., 'US', 'CA', 'GB') or null to disable
 * @returns Array of holidays for that month
 */
export function useHolidays(
  year: number,
  month: number,
  region: string | null
): Holiday[] {
  const [holidays, setHolidays] = useState<Holiday[]>([]);

  useEffect(() => {
    if (!region) {
      setHolidays([]);
      return;
    }

    let cancelled = false;

    async function loadHolidays() {
      const Holidays = await getHolidaysClass();
      if (!Holidays || cancelled || !region) {
        return;
      }

      try {
        const hd = new Holidays(region);
        const allHolidays = hd.getHolidays(year);

        // Filter to current month and map to our format
        const monthHolidays: Holiday[] = [];

        for (const h of allHolidays) {
          const date = new Date(h.date);
          if (date.getMonth() === month) {
            monthHolidays.push({
              date,
              name: h.name,
            });
          }
        }

        if (!cancelled) {
          setHolidays(monthHolidays);
        }
      } catch (err) {
        console.error(
          `[Holidays] Failed to load holidays for region ${region}:`,
          err
        );
        if (!cancelled) {
          setHolidays([]);
        }
      }
    }

    loadHolidays();

    return () => {
      cancelled = true;
    };
  }, [year, month, region]);

  return holidays;
}

/**
 * Hook to get holidays grouped by day number for easy lookup.
 * Returns a Map where key is day of month (1-31) and value is array of holiday names.
 */
export function useHolidaysByDay(
  year: number,
  month: number,
  region: string | null
): Map<number, string[]> {
  const holidays = useHolidays(year, month, region);

  return useMemo(() => {
    const map = new Map<number, string[]>();

    for (const holiday of holidays) {
      const day = holiday.date.getDate();
      const existing = map.get(day) || [];
      existing.push(holiday.name);
      map.set(day, existing);
    }

    return map;
  }, [holidays]);
}
